"use client"

import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  MoreHorizontal,
  Search,
  SlidersHorizontal,
  UserCheck,
  UserPlus,
  UserX,
  X,
} from "lucide-react"
import { Badge } from "../ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog"
import { adminDashboard } from '../../services/api'

// Interface pour les inscriptions
interface Inscription {
  id: number
  fullName: string
  phoneNumber: string | null
  email: string
  status: string
  demandeDate: string
  approbationDate: string | null
  formationId: number
  formationName: string
  createdAt: string
  updatedAt: string
}

// Interface pour les formations
interface Formation {
  id: number
  name: string
}

// Interface pour les détails utilisateur
interface UserDetails {
  id: number
  name: string
  email: string
  phone: string
  course: string
  requestDate: string
  status: string
  approvalDate?: string
  rejectionDate?: string
}

export default function AdminInscriptions() {
  const [inscriptions, setInscriptions] = useState<Inscription[]>([])
  const [formations, setFormations] = useState<Formation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())
  
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [courseFilter, setCourseFilter] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null)
  const [activeTab, setActiveTab] = useState("all")

  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await adminDashboard.getAllInscriptions()
        if (response.data.success) {
          setInscriptions(response.data.inscriptions)
          setFormations(response.data.formations)
        }
      } catch (err) {
        console.error('Erreur lors du chargement des inscriptions:', err)
        setError('Erreur lors du chargement des inscriptions')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Fonction pour recharger les données
  const refreshData = async () => {
    try {
      const response = await adminDashboard.getAllInscriptions()
      if (response.data.success) {
        setInscriptions(response.data.inscriptions)
        setFormations(response.data.formations)
      }
    } catch (err) {
      console.error('Erreur lors du rechargement des inscriptions:', err)
    }
  }

  // Fonction pour formater la date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-"
    
    try {
      const date = new Date(dateString)
      
      if (isNaN(date.getTime())) {
        return dateString
      }
      
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      
      return `${day}/${month}/${year}`
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error)
      return dateString
    }
  }

  // Fonction pour gérer les actions d'approbation/rejet
  const handleInscriptionAction = async (inscriptionId: number, action: 'approuve' | 'reject') => {
    try {
      setProcessingIds(prev => new Set(prev).add(inscriptionId))
      
      await adminDashboard.approveOrRejectInscription(inscriptionId, action)
      
      // Recharger les données après l'action
      await refreshData()
      
    } catch (err) {
      console.error(`Erreur lors de l'action ${action} sur l'inscription:`, err)
      setError(`Erreur lors de l'action ${action} sur l'inscription`)
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(inscriptionId)
        return newSet
      })
    }
  }

  // Ouvrir le modal avec les détails utilisateur
  const openUserDetails = (inscription: Inscription) => {
    setSelectedUser({
      id: inscription.id,
      name: inscription.fullName,
      email: inscription.email,
      phone: inscription.phoneNumber || "Non disponible",
      course: inscription.formationName,
      requestDate: formatDate(inscription.demandeDate),
      status: inscription.status,
      approvalDate: inscription.approbationDate ? formatDate(inscription.approbationDate) : undefined,
      rejectionDate: inscription.status === "Rejetée" && inscription.approbationDate ? formatDate(inscription.approbationDate) : undefined
    })
    setIsDetailsModalOpen(true)
  }

  // Filtrer les inscriptions selon l'onglet actif
  const getFilteredInscriptionsByTab = (tabValue: string) => {
    let baseInscriptions = inscriptions

    // Filtrer par onglet
    if (tabValue !== "all") {
      const statusMap: { [key: string]: string } = {
        "pending": "En attente",
        "approved": "Approuvée", 
        "rejected": "Rejetée"
      }
      baseInscriptions = inscriptions.filter(inscription => inscription.status === statusMap[tabValue])
    }

    // Appliquer les autres filtres
    return baseInscriptions.filter((inscription) => {
      // Filtrer par terme de recherche
      const matchesSearch =
        inscription.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inscription.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inscription.formationName.toLowerCase().includes(searchTerm.toLowerCase())

      // Filtrer par statut (uniquement pour l'onglet "all")
      const matchesStatus = tabValue !== "all" || statusFilter === null || statusFilter === "all" || 
        inscription.status === getStatusValue(statusFilter)

      // Filtrer par cours
      const matchesCourse = courseFilter === null || courseFilter === "all" || 
        inscription.formationId.toString() === courseFilter

      return matchesSearch && matchesStatus && matchesCourse
    })
  }

  // Obtenir la valeur du statut pour le filtrage
  const getStatusValue = (filterValue: string) => {
    switch (filterValue) {
      case "pending": return "En attente"
      case "approved": return "Approuvée"
      case "rejected": return "Rejetée"
      default: return filterValue
    }
  }

  // Filtrer les inscriptions
  const filteredInscriptions = getFilteredInscriptionsByTab(activeTab)

  // Pagination
  const totalPages = Math.ceil(filteredInscriptions.length / itemsPerPage)
  const paginatedInscriptions = filteredInscriptions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, courseFilter, itemsPerPage, activeTab])

  // Obtenir la traduction du statut
  const getStatusLabel = (status: string) => {
    return status // Les statuts viennent déjà en français de l'API
  }

  // Obtenir la classe CSS pour le badge de statut
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Approuvée": return "bg-green-50 text-green-700 border-green-200"
      case "En attente": return "bg-yellow-50 text-yellow-700 border-yellow-200"
      case "Rejetée": return "bg-red-50 text-red-700 border-red-200"
      default: return ""
    }
  }

  // Compter les inscriptions par statut
  const pendingCount = inscriptions.filter(i => i.status === "En attente").length
  const approvedCount = inscriptions.filter(i => i.status === "Approuvée").length
  const rejectedCount = inscriptions.filter(i => i.status === "Rejetée").length

  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-500">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold tracking-tight">Gestion des inscriptions</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Gérez les inscriptions aux formations et attribuez des accès aux utilisateurs.
            </p>
          </div>
          <div className="flex gap-2">
            <a href="/admin/inscriptions/new">
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Nouvelle inscription
              </Button>
            </a>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="all">Toutes ({inscriptions.length})</TabsTrigger>
            <TabsTrigger value="pending">En attente ({pendingCount})</TabsTrigger>
            <TabsTrigger value="approved">Approuvées ({approvedCount})</TabsTrigger>
            <TabsTrigger value="rejected">Rejetées ({rejectedCount})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Liste des inscriptions</CardTitle>
                <CardDescription>{filteredInscriptions.length} inscriptions trouvées</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative w-full sm:w-64">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <Input
                        type="search"
                        placeholder="Rechercher..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Select value={statusFilter || ""} onValueChange={(value) => setStatusFilter(value || null)}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Tous les statuts" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="approved">Approuvée</SelectItem>
                          <SelectItem value="rejected">Rejetée</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={courseFilter || ""} onValueChange={(value) => setCourseFilter(value || null)}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Toutes les formations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes les formations</SelectItem>
                          {formations.map((formation) => (
                            <SelectItem key={formation.id} value={formation.id.toString()}>
                              {formation.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="icon">
                        <SlidersHorizontal className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">Afficher</p>
                    <Select value={String(itemsPerPage)} onValueChange={(value) => setItemsPerPage(Number(value))}>
                      <SelectTrigger className="w-[80px]">
                        <SelectValue placeholder={itemsPerPage} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">par page</p>
                  </div>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Formation</TableHead>
                        <TableHead>Date de demande</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedInscriptions.length > 0 ? (
                        paginatedInscriptions.map((inscription) => (
                          <TableRow key={inscription.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{inscription.fullName}</div>
                                <div className="text-sm text-muted-foreground">{inscription.email}</div>
                              </div>
                            </TableCell>
                            <TableCell>{inscription.formationName}</TableCell>
                            <TableCell>{formatDate(inscription.demandeDate)}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={getStatusBadgeClass(inscription.status)}
                              >
                                {getStatusLabel(inscription.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    disabled={processingIds.has(inscription.id)}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openUserDetails(inscription)}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    Voir les détails
                                  </DropdownMenuItem>
                                  {inscription.status === "En attente" && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleInscriptionAction(inscription.id, 'approuve')}
                                        disabled={processingIds.has(inscription.id)}
                                      >
                                        <UserCheck className="mr-2 h-4 w-4" />
                                        {processingIds.has(inscription.id) ? 'Traitement...' : 'Approuver'}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleInscriptionAction(inscription.id, 'reject')}
                                        disabled={processingIds.has(inscription.id)}
                                      >
                                        <UserX className="mr-2 h-4 w-4" />
                                        {processingIds.has(inscription.id) ? 'Traitement...' : 'Rejeter'}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                            Aucune inscription trouvée
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
                      {Math.min(currentPage * itemsPerPage, filteredInscriptions.length)} sur {filteredInscriptions.length}{" "}
                      inscriptions
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="text-sm">
                        Page {currentPage} sur {totalPages}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Inscriptions en attente</CardTitle>
                <CardDescription>
                  {pendingCount} inscriptions en attente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Formation</TableHead>
                        <TableHead>Date de demande</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInscriptions.length > 0 ? (
                        filteredInscriptions.map((inscription) => (
                          <TableRow key={inscription.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{inscription.fullName}</div>
                                <div className="text-sm text-muted-foreground">{inscription.email}</div>
                              </div>
                            </TableCell>
                            <TableCell>{inscription.formationName}</TableCell>
                            <TableCell>{formatDate(inscription.demandeDate)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openUserDetails(inscription)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Détails
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="default" 
                                  onClick={() => handleInscriptionAction(inscription.id, 'approuve')}
                                  disabled={processingIds.has(inscription.id)}
                                >
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  {processingIds.has(inscription.id) ? 'Traitement...' : 'Approuver'}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  onClick={() => handleInscriptionAction(inscription.id, 'reject')}
                                  disabled={processingIds.has(inscription.id)}
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  {processingIds.has(inscription.id) ? 'Traitement...' : 'Rejeter'}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                            Aucune inscription en attente
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Inscriptions approuvées</CardTitle>
                <CardDescription>
                  {approvedCount} inscriptions approuvées
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Formation</TableHead>
                        <TableHead>Date d'approbation</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInscriptions.length > 0 ? (
                        filteredInscriptions.map((inscription) => (
                          <TableRow key={inscription.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{inscription.fullName}</div>
                                <div className="text-sm text-muted-foreground">{inscription.email}</div>
                              </div>
                            </TableCell>
                            <TableCell>{inscription.formationName}</TableCell>
                            <TableCell>{formatDate(inscription.approbationDate)}</TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="outline" onClick={() => openUserDetails(inscription)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Détails
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                            Aucune inscription approuvée
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rejected" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Inscriptions rejetées</CardTitle>
                <CardDescription>
                  {rejectedCount} inscriptions rejetées
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Formation</TableHead>
                        <TableHead>Date de rejet</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInscriptions.length > 0 ? (
                        filteredInscriptions.map((inscription) => (
                          <TableRow key={inscription.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{inscription.fullName}</div>
                                <div className="text-sm text-muted-foreground">{inscription.email}</div>
                              </div>
                            </TableCell>
                            <TableCell>{inscription.formationName}</TableCell>
                            <TableCell>{formatDate(inscription.approbationDate)}</TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="outline" onClick={() => openUserDetails(inscription)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Détails
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                            Aucune inscription rejetée
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de détails utilisateur */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Détails de l'inscription</DialogTitle>
            <DialogDescription>
              Informations complètes sur l'apprenant et sa demande d'inscription
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Informations personnelles</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Nom complet :</p>
                    <p className="text-sm">{selectedUser.name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Email :</p>
                    <p className="text-sm">{selectedUser.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Téléphone :</p>
                    <p className="text-sm">{selectedUser.phone}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Détails de l'inscription</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Formation :</p>
                    <p className="text-sm">{selectedUser.course}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Date de demande :</p>
                    <p className="text-sm">{selectedUser.requestDate}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Statut :</p>
                    <Badge variant="outline" className={getStatusBadgeClass(selectedUser.status)}>
                      {getStatusLabel(selectedUser.status)}
                    </Badge>
                  </div>
                  {selectedUser.approvalDate && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Date d'approbation :</p>
                      <p className="text-sm">{selectedUser.approvalDate}</p>
                    </div>
                  )}
                  {selectedUser.rejectionDate && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Date de rejet :</p>
                      <p className="text-sm">{selectedUser.rejectionDate}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-end">
            <Button type="button" variant="secondary" onClick={() => setIsDetailsModalOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}